import React from 'react';
import styled, { css } from 'styled-components';
import { useLanguage } from '../context/LanguageContext';
import { useInView } from '../hooks/useInView';

const Section = styled.section`
  padding: 120px 0;
  background: #FFFDF7;
  @media (min-width: 768px) { padding: 160px 0; }
`;

const Container = styled.div`
  max-width: 1280px;
  margin: 0 auto;
  padding: 0 32px;
  transition: opacity 1s ease, transform 1s ease;
  ${({ $inView }) =>
    $inView
      ? css`opacity: 1; transform: translateY(0);`
      : css`opacity: 0; transform: translateY(40px);`}
`;

const Grid = styled.div`
  display: grid;
  gap: 56px;
  @media (min-width: 1024px) {
    grid-template-columns: 1fr 1fr;
    gap: 100px;
    align-items: center;
  }
`;

const TextSide = styled.div`
  order: 2;
  @media (min-width: 1024px) { order: 1; }
`;

const ImageSide = styled.div`
  order: 1;
  @media (min-width: 1024px) { order: 2; }
`;

const Eyebrow = styled.p`
  font-size: 0.72rem;
  font-weight: 600;
  letter-spacing: 0.2em;
  text-transform: uppercase;
  color: #C07020;
  margin-bottom: 16px;
`;

const Title = styled.h2`
  font-family: 'Cormorant Garamond', serif;
  font-size: clamp(2.4rem, 5vw, 3.8rem);
  color: #1A1208;
  margin-bottom: 28px;
  line-height: 1.05;
  letter-spacing: -0.01em;
`;

const Divider = styled.div`
  width: 48px;
  height: 2px;
  background: #C07020;
  margin-bottom: 28px;
  border-radius: 1px;
`;

const Body = styled.p`
  font-size: 1.05rem;
  color: rgba(26,18,8,0.78);
  line-height: 1.85;
  margin-bottom: 52px;
`;

const Timeline = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 36px;
`;

const TimelineItem = styled.div`
  transition: opacity 0.7s ease, transform 0.7s ease;
  transition-delay: ${({ $delay }) => $delay}ms;
  ${({ $inView }) =>
    $inView
      ? css`opacity: 1; transform: translateX(0);`
      : css`opacity: 0; transform: translateX(-16px);`}
`;

const Year = styled.div`
  font-family: 'Cormorant Garamond', serif;
  font-size: clamp(2rem, 4vw, 2.6rem);
  font-weight: 600;
  color: #7C3A00;
  line-height: 1;
`;

const YearLabel = styled.div`
  font-size: 0.75rem;
  font-weight: 500;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: rgba(26,18,8,0.58);
  margin-top: 6px;
`;

const StoryImage = styled.div`
  border-radius: 20px;
  overflow: hidden;
  aspect-ratio: 4/5;
  box-shadow: 0 32px 80px rgba(26,18,8,0.12);
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: transform 0.8s cubic-bezier(0.16,1,0.3,1);
  }
  &:hover img { transform: scale(1.03); }
`;

const timeline = [
  { year: '1950', en: 'Origin',           es: 'Origen' },
  { year: '1985', en: 'Second Generation', es: 'Segunda Generación' },
  { year: '2024', en: 'Export to the U.S.',es: 'Exportación a EE. UU.' },
];

export function Story() {
  const { t } = useLanguage();
  const { ref, isInView } = useInView({ threshold: 0.2 });

  return (
    <Section id="historia" aria-label="Nuestra historia">
      <Container ref={ref} $inView={isInView}>
        <Grid>
          <TextSide>
            <Eyebrow>{t('Our Story', 'Nuestra Historia')}</Eyebrow>
            <Title>{t('A Flavor Rooted in Tradition', 'Un Sabor con Raíces')}</Title>
            <Divider />
            <Body>
              {t(
                "At Hacienda Real, making cajeta is not just a tradition – it's a legacy passed down through generations. Since 1950, our family has been crafting this traditional Mexican caramel using 100% cow's milk and no preservatives. From our founder Don Mario's kitchen in San Juan de los Lagos, Jalisco, to homes across the world.",
                'En Hacienda Real, hacer cajeta no es solo una tradición, sino un legado que ha pasado de generación en generación. Desde 1950, nuestra familia ha preparado este caramelo mexicano utilizando 100% leche de vaca y sin conservadores. Desde la cocina de Don Mario en San Juan de los Lagos, Jalisco, hasta hogares en todo el mundo.'
              )}
            </Body>
            <Timeline>
              {timeline.map((item, i) => (
                <TimelineItem key={item.year} $inView={isInView} $delay={i * 200 + 400}>
                  <Year>{item.year}</Year>
                  <YearLabel>{t(item.en, item.es)}</YearLabel>
                </TimelineItem>
              ))}
            </Timeline>
          </TextSide>

          <ImageSide>
            <StoryImage>
              <img
                src="/images/cajetaenpasto.jpeg"
                alt={t('Traditional cajeta making', 'Elaboración tradicional de cajeta')}
                loading="lazy"
              />
            </StoryImage>
          </ImageSide>
        </Grid>
      </Container>
    </Section>
  );
}
